import { type Dispatch, type SetStateAction, useContext } from 'react'

import BrokerContext from 'components/context/BrokerContext'
import { SplitFilterButton } from 'components/reusable/SplitFilterButton'
import TooltipWrapper from 'components/reusable/TooltipWrapper'
import { Button } from 'slab'
import { type BrokersBroker } from 'utils/web_api.schemas'

import { type WorkflowsFilters } from './workflowsHelpers'

type WorkflowsQuickFiltersProps = {
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
}

// Quick filter helper for workflows with the next task = manual
const nextTaskManualAndPending = {
  next_task_execution_methods: ['manual'],
  next_task_statuses: ['pending'],
}

const nextTaskManualForOpsAction = {
  ...nextTaskManualAndPending,
  next_task_execution_contexts: ['thatch'],
}

// Quick filter for workflows with manual thatch (ops) next tasks
function setUnprocessedEnrollmentsQueue (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters(() => ({
    workflow_types: [
      'submission',
      'renewal',
      'plan_change',
      'dependent_change',
      'plan_change_and_dependent_change',
      'off_thatch',
    ],
    ...nextTaskManualForOpsAction,
  }))
}

// Quick filter for workflows with manual member next tasks
function setBlockedEnrollmentsQueue (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters(() => ({
    workflow_types: [
      'submission',
      'renewal',
      'plan_change',
      'dependent_change',
      'plan_change_and_dependent_change',
      'off_thatch',
    ],
    ...nextTaskManualAndPending,
    next_task_execution_contexts: ['member'],
  }))
}

function setOffboardingEscalated (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters(() => ({
    workflow_types: ['offboarding'],
    ...nextTaskManualForOpsAction,
  }))
}

function setCancellationsQueue (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters(() => ({
    workflow_types: ['cancellation'],
    ...nextTaskManualForOpsAction,
  }))
}

// Quick filter for workflows with failed next tasks
function setFailedTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters(() => ({
    next_task_statuses: ['failed'],
  }))
}

// Quick filter for workflows with manual next tasks
function setManualTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    workflow_task_execution_methods: ['manual'],
  }))
}

// Quick filter for workflows with automated next tasks
function setAutomatedTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    workflow_task_execution_methods: ['automated'],
  }))
}

// Quick filter for workflows with internal next tasks
function setThatchTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    workflow_task_execution_contexts: ['thatch'],
  }))
}

// Quick filter for workflows with external next tasks
function setMemberTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    workflow_task_execution_contexts: ['member'],
  }))
}

// Quick filter for in-progress workflows
function setInProgressWorkflows (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    statuses: ['in_progress'],
  }))
}

// Quick filter for paused workflows
function setPausedWorkflows (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    statuses: ['paused'],
  }))
}

// Quick filter for medical workflows
function setCoverageTypeMedical (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    coverage_types: ['medical'],
  }))
}

// Quick filter for ancillaries workflows
function setCoverageTypeAncillaries (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    coverage_types: ['dental', 'vision'],
  }))
}

function setEmployerReviewStatusPendingFilters (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    employer_review_statuses: ['pending'],
  }))
}

function setAssignedToMeFilters (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>,
  currentBroker: BrokersBroker | undefined
) {
  setFilters((prev) => ({
    ...prev,
    ...(currentBroker ? { broker_ids: [currentBroker?.id] } : {}),
  }))
}

function setUnassignedFilters (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    broker_ids: ['unassigned'],
  }))
}

// Helper function to set outstanding task filters by category
function setOutstandingTaskFilters (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>,
  category: 'submission' | 'payment' | 'autopay'
) {
  setFilters(() => ({
    workflow_task_categories: [category],
    workflow_task_statuses: ['pending', 'processing', 'excluded'],
  }))
}

const WorkflowsQuickFilters = ({ setFilters }: WorkflowsQuickFiltersProps) => {
  const { currentBroker } = useContext(BrokerContext)

  return (
    <div className='ml-1 flex items-center justify-start gap-2 flex-shrink-0 flex-wrap'>
      <div className='text-sm font-semibold min-w-16'>Quick filters</div>
      <SplitFilterButton
        variant='primary'
        tooltip='Filter workflows by outstanding tasks in a specific category (submission, payment, or autopay)'
        primaryOption={{
          label: 'Outstanding Submission Tasks',
          handleSelect: () => setOutstandingTaskFilters(setFilters, 'submission'),
        }}
        additionalOptions={[
          {
            label: 'Outstanding Payment Tasks',
            handleSelect: () => setOutstandingTaskFilters(setFilters, 'payment'),
          },
          {
            label: 'Outstanding Autopay Tasks',
            handleSelect: () => setOutstandingTaskFilters(setFilters, 'autopay'),
          },
        ]}
      />
      <TooltipWrapper tooltipText='Enrollments with manual tasks waiting to be processed by Ops'>
        <Button
          variant='tertiary'
          onClick={() => setUnprocessedEnrollmentsQueue(setFilters)}
        >
          Unprocessed Enrollments
        </Button>
      </TooltipWrapper>
      <TooltipWrapper tooltipText='Enrollments with manual tasks waiting to be processed by the Member that are blocking the rest of the submission process'>
        <Button
          variant='tertiary'
          onClick={() => setBlockedEnrollmentsQueue(setFilters)}
        >
          Blocked Enrollments
        </Button>
      </TooltipWrapper>
      <TooltipWrapper tooltipText='Cancelled enrollments to be processed by Ops'>
        <Button
          variant='tertiary'
          onClick={() => setCancellationsQueue(setFilters)}
        >
          Cancelled Enrollments
        </Button>
      </TooltipWrapper>
      <TooltipWrapper tooltipText='Offboarded enrollments that are overpaid/underpaid and require Ops action to resolve'>
        <Button
          variant='tertiary'
          onClick={() => setOffboardingEscalated(setFilters)}
        >
          Offboarding Escalated
        </Button>
      </TooltipWrapper>
      <TooltipWrapper tooltipText='Workflows with failed tasks'>
        <Button variant='tertiary' onClick={() => setFailedTasks(setFilters)}>
          Failed
        </Button>
      </TooltipWrapper>
      <SplitFilterButton
        variant='tertiary'
        tooltip='Filter workflow tasks by execution method (Manual vs. Automated)'
        primaryOption={{
          label: 'Method: Manual',
          handleSelect: () => setManualTasks(setFilters),
        }}
        additionalOptions={[
          {
            label: 'Method: Automated',
            handleSelect: () => setAutomatedTasks(setFilters),
          },
        ]}
      />
      <SplitFilterButton
        variant='tertiary'
        tooltip='Filter workflow tasks by execution context (Ops vs. Member)'
        primaryOption={{
          label: 'Execution: Ops',
          handleSelect: () => setThatchTasks(setFilters),
        }}
        additionalOptions={[
          {
            label: 'Execution: Member',
            handleSelect: () => setMemberTasks(setFilters),
          },
        ]}
      />
      <SplitFilterButton
        variant='tertiary'
        tooltip='Filter workflows by workflow status'
        primaryOption={{
          label: 'Status: In Progress',
          handleSelect: () => setInProgressWorkflows(setFilters),
        }}
        additionalOptions={[
          {
            label: 'Status: Paused',
            handleSelect: () => setPausedWorkflows(setFilters),
          },
        ]}
      />
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setCoverageTypeMedical(setFilters)}
      >
        Medical
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setCoverageTypeAncillaries(setFilters)}
      >
        Ancillaries
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setEmployerReviewStatusPendingFilters(setFilters)}
      >
        Pending Employer Review
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setAssignedToMeFilters(setFilters, currentBroker)}
      >
        Assigned to me
      </Button>
      <Button
        variant='tertiary-deemphasized'
        onClick={() => setUnassignedFilters(setFilters)}
      >
        Unassigned
      </Button>
    </div>
  )
}

export default WorkflowsQuickFilters
